import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	PetitionDetailsRecentSignersData,
	type PetitionDetailsRecentSignersDataQuery,
	type PetitionDetailsRecentSignersDataQueryVariables,
} from './recentSignersData.graphql';

export type RecentSignersData = NonNullable<PetitionDetailsRecentSignersDataQuery['petition']>;

// TODO handle errors in the UI instead of falling back to empty data — see CHANGE-97098
const EMPTY_RECENT_SIGNERS_DATA: RecentSignersData = {
	recentPublicSignersConnectionV2: {
		edges: [],
	},
};

/**
 * Each edge's user is resolved via an unbatched per-edge lookup that can time out under load,
 * which nulls the whole `petition` field via GraphQL non-null propagation. Fetching this as its
 * own request (instead of as part of the main PetitionDetailsData query) means a timed-out lookup
 * only degrades this section to an empty list, instead of failing the whole page — mirrors the
 * discussion_service isolation in discussionData/ (CHANGE-95607).
 */
export async function getPetitionRecentSignersData(
	utilityContext: UtilityContext,
	variables: PetitionDetailsRecentSignersDataQueryVariables,
): Promise<RecentSignersData> {
	try {
		const { data } = await utilityContext.gql.fetch<
			PetitionDetailsRecentSignersDataQuery,
			PetitionDetailsRecentSignersDataQueryVariables
		>({
			query: PetitionDetailsRecentSignersData,
			variables,
			rejectOnError: true,
			// to isolate from other batched queries — see pageData/api.ts
			batched: false,
		});

		return data.petition ?? EMPTY_RECENT_SIGNERS_DATA;
	} catch {
		return EMPTY_RECENT_SIGNERS_DATA;
	}
}
